import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RouteComponent } from '@app/route/route.component';
import { HeaderService } from '@app/service/header/header.service';
import { StateService } from '@app/service/state/state.service';
import Translations from './fi-loan-eligibility-questionnaire-extended.component.json';
import { FiUserService } from '@app/service/fi-user-service/fi-user.service';
import {
  ListOption,
  PillSelectorOption,
  StepperComponent,
} from 'web-component-library/projects/component-library/src/public-api';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FormValidators } from '@app/validation/validators';
import { FiRoute } from '@assets/val/route-constants';
import {
  KalpAnswersFI,
  KalpInfoReason,
  LeqService,
  MartialStatus,
  Occupation,
  TypeOfAccommodation,
} from '@app/service/leq/leq.service';
import { CurrencyCode } from '@app/model/currency';
import { formatDate } from '@angular/common';

@Component({
  selector: 'fi-loan-eligibility-questionnaire-extended',
  templateUrl: './fi-loan-eligibility-questionnaire-extended.component.html',
  styleUrls: [
    '../../page-shared.css',
    './fi-loan-eligibility-questionnaire-extended.component.scss',
  ],
})
export class FiLoanEligibilityQuestionnaireExtendedComponent
  extends RouteComponent
  implements OnInit
{
  @ViewChild(StepperComponent) stepper!: StepperComponent;

  formGroup: FormGroup;
  showError = false;

  loading: boolean = true;
  error: boolean = false;
  navigationInProgress: boolean;
  shouldShowMortgageInputs: boolean = false;
  shouldDisableEndDate: boolean = true;
  viewReviewAnswers: boolean = false;
  shouldShowOccupationInputs: boolean = true;

  translations: { [key: string]: string };

  kalpAnswers: KalpAnswersFI;
  currencySymbol: string = ' €';

  // Pill selector data
  occupationOptions: PillSelectorOption[];
  selectedOccupationOption: PillSelectorOption;
  martialStatusOptions: PillSelectorOption[];
  selectedMartialStatusOption: PillSelectorOption;
  typeOfAccommodationOptions: PillSelectorOption[];
  selectedTypeOfAccommodationOption: PillSelectorOption;

  // Date picker data
  minDueDate: string;
  maxDueDate: string;
  initialDate: Date;

  // Slider data
  sliderAnswer: number;
  leqKidsAnswers: ListOption[];

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    private fiUserService: FiUserService,
    private leqService: LeqService
  ) {
    super(router, route, stateService, headerService);
  }

  async ngOnInit(): Promise<void> {
    this.translations =
      Translations[
        this.fiUserService.getCurrentStateLocaleBaseCode(this.state.language)
      ];

    await this.setup();
  }

  async setup(): Promise<void> {
    try {
      this.fiUserService.getKalpInfo(KalpInfoReason.INFO).subscribe((resp) => {
        if (resp) {
          this.leqService.setKalpAnswersFI(resp);
          this.kalpAnswers = resp;
        }

        this.initializePillAndSliderData();
        this.initializeFormGroup();
        this.loading = false;
      });
    } catch (err) {
      this.error = true;
      this.handleError(err);
    }
  }

  initializeFormGroup(): void {
    this.formGroup = new FormGroup(
      {
        monthlyNetIncome: new FormControl(
          String(this.kalpAnswers?.monthlyNetIncome || ''),
          [Validators.required, FormValidators.number(0, 999999)]
        ),
        startDate: new FormControl(
          this.kalpAnswers?.employmentStartDate
            ? formatDate(
                this.kalpAnswers.employmentStartDate,
                'yyyy-MM-dd',
                'en'
              )
            : '',
          [Validators.required]
        ),
        endDate: new FormControl(
          this.kalpAnswers?.employmentEndDate
            ? formatDate(this.kalpAnswers.employmentEndDate, 'yyyy-MM-dd', 'en')
            : '',
          !this.shouldDisableEndDate ? [Validators.required] : []
        ),
        nameOfEmployer: new FormControl(
          this.kalpAnswers?.nameOfEmployer || '',
          [Validators.required, Validators.maxLength(255)]
        ),
        rentOrFeePerMonth: new FormControl(
          String(this.kalpAnswers?.rentOrFeePerMonth || ''),
          [Validators.required, FormValidators.number(0, 999999)]
        ),
        totalAmountOfOtherLoans: new FormControl(
          String(this.kalpAnswers?.totalAmountOfOtherLoans || ''),
          [Validators.required, FormValidators.number(0, 999999)]
        ),
        monthlyObligationForOtherLoans: new FormControl(
          String(this.kalpAnswers?.monthlyObligationForOtherLoans || ''),
          [Validators.required, FormValidators.number(0, 999999)]
        ),
        monthlyLivingExpenses: new FormControl(
          String(this.kalpAnswers?.monthlyLivingExpenses || ''),
          [Validators.required, FormValidators.number(0, 999999)]
        ),
      },
      { validators: this.dateValidation }
    );

    this.onSelectedAccommodationOptionChange();
    this.onSelectedOccupationOptionChange(this.selectedOccupationOption);
  }

  initializePillAndSliderData(): void {
    this.occupationOptions = this.getOccupationOptions();
    this.martialStatusOptions = this.getMartialStatusOptions();
    this.typeOfAccommodationOptions = this.getTypeOfAccommodationOtions();

    this.selectedOccupationOption = this.kalpAnswers?.occupation
      ? this.findSelectedOptionByValue(
          this.occupationOptions,
          this.kalpAnswers.occupation
        )
      : this.occupationOptions[0];
    this.selectedMartialStatusOption = this.kalpAnswers?.martialStatus
      ? this.findSelectedOptionByValue(
          this.martialStatusOptions,
          this.kalpAnswers.martialStatus
        )
      : this.martialStatusOptions[0];
    this.selectedTypeOfAccommodationOption = this.kalpAnswers
      ?.typeOfAccommodation
      ? this.findSelectedOptionByValue(
          this.typeOfAccommodationOptions,
          this.kalpAnswers.typeOfAccommodation
        )
      : this.typeOfAccommodationOptions[0];

    this.leqKidsAnswers = [
      { label: '0', value: 0 },
      { label: '1', value: 1 },
      { label: '2', value: 2 },
      { label: '3', value: 3 },
      { label: '4', value: 4 },
      { label: '5', value: 5 },
      { label: '6', value: 6 },
      { label: '7', value: 7 },
      { label: '8', value: 8 },
      { label: '9', value: 9 },
      { label: '10', value: 10 },
      { label: this.translations.sliderAboveTenChildren, value: 11 },
    ];
    this.sliderAnswer = this.kalpAnswers?.amountOfChildren ?? 2;
  }

  findSelectedOptionByValue(
    pillOptions: PillSelectorOption[],
    pillOptionValue: Occupation | MartialStatus | TypeOfAccommodation
  ): PillSelectorOption {
    return pillOptions.find(
      (pillOption) => pillOption.value === pillOptionValue
    );
  }

  getOccupationOptions(): PillSelectorOption[] {
    return [
      {
        text: this.translations.occupationFullTime,
        value: Occupation.FULL_TIME_EMPLOYMENT,
      },
      {
        text: this.translations.occupationPartTime,
        value: Occupation.PART_TIME_EMPLOYMENT,
      },
      {
        text: this.translations.occupationFixedTerm,
        value: Occupation.FIX_TERM_EMPLOYMENT,
      },
      {
        text: this.translations.occupationSelfemployed,
        value: Occupation.SELF_EMPLOYED,
      },
      {
        text: this.translations.occupationStudent,
        value: Occupation.STUDENT,
      },
      {
        text: this.translations.occupationRetired,
        value: Occupation.RETIRED,
      },
      {
        text: this.translations.occupationUnemployed,
        value: Occupation.UNEMPLOYED,
      },
    ];
  }

  getMartialStatusOptions(): PillSelectorOption[] {
    return [
      {
        text: this.translations.martialStatusSingle,
        value: MartialStatus.SINGLE,
      },
      {
        text: this.translations.martialStatusMarried,
        value: MartialStatus.MARRIED,
      },
      {
        text: this.translations.martialStatusCohabitant,
        value: MartialStatus.COHABITANT,
      },
    ];
  }

  getTypeOfAccommodationOtions(): PillSelectorOption[] {
    return [
      {
        text: this.translations.typeOfAccommodationRental,
        value: TypeOfAccommodation.RENT,
      },
      {
        text: this.translations.typeOfAccommodationOwner,
        value: TypeOfAccommodation.OWNER,
      },
      {
        text: this.translations.typeOfAccommodationPartnerOwned,
        value: TypeOfAccommodation.PARTNER_OWNS_PROPERTY,
      },
      {
        text: this.translations.typeOfAccommodationOther,
        value: TypeOfAccommodation.OTHER,
      },
    ];
  }

  resetEndDateForm(): void {
    if (this.formGroup?.controls['endDate']?.value) {
      this.formGroup.controls['endDate'].setValue('');
    }
  }

  backToPreviousStep(): void {
    if (this.viewReviewAnswers) {
      this.viewReviewAnswers = false;
    } else {
      this.stepper.previousStep();
    }
  }

  async handleCtaClick(): Promise<void> {
    if (this.stepper.currentStepIndex < this.stepper.steps.length - 1) {
      this.stepper.nextStep();
      return;
    }

    if (this.formGroup.valid) {
      await this.createAndSetKalpAnswers();
      this.kalpAnswers = this.leqService.getKalpAnswersFI();
      this.viewReviewAnswers = true;
    } else {
      this.stepper.goToStep(0);
      this.showError = true;
    }
  }

  async handleCancellation(): Promise<void> {
    this.navigationInProgress = true;
    this.router.navigate([FiRoute.HOME], {
      queryParamsHandling: 'merge',
    });
    this.navigationInProgress = false;
  }

  async createAndSetKalpAnswers(): Promise<void> {
    const kalpAnswers: KalpAnswersFI = {
      occupation: Occupation[this.selectedOccupationOption.value],
      monthlyNetIncome: this.getNumberFromDecimalString(
        this.formGroup.controls['monthlyNetIncome'].value
      ),
      nameOfEmployer: this.formGroup.controls['nameOfEmployer']?.value || null,
      employmentStartDate:
        new Date(this.formGroup.controls['startDate']?.value) || null,
      employmentEndDate:
        new Date(this.formGroup.controls['endDate']?.value) || null,
      martialStatus: MartialStatus[this.selectedMartialStatusOption.value],
      typeOfAccommodation:
        TypeOfAccommodation[this.selectedTypeOfAccommodationOption.value],
      amountOfChildren: this.sliderAnswer,
      rentOrFeePerMonth: this.getNumberFromDecimalString(
        this.formGroup.controls['rentOrFeePerMonth'].value
      ),
      totalMortgage:
        this.getNumberFromDecimalString(
          this.formGroup.controls['totalMortgage']?.value
        ) || 0,
      mortgagePaymentPerMonth:
        this.getNumberFromDecimalString(
          this.formGroup.controls['mortgagePaymentPerMonth']?.value
        ) || 0,
      totalAmountOfOtherLoans: this.getNumberFromDecimalString(
        this.formGroup.controls['totalAmountOfOtherLoans'].value
      ),
      monthlyObligationForOtherLoans: this.getNumberFromDecimalString(
        this.formGroup.controls['monthlyObligationForOtherLoans'].value
      ),
      monthlyLivingExpenses: this.getNumberFromDecimalString(
        this.formGroup.controls['monthlyLivingExpenses'].value
      ),
      currencyCode: CurrencyCode.EUR,
    };

    this.leqService.setKalpAnswersFI(kalpAnswers);
  }

  isAccommondationOwner(): boolean {
    return (
      this.selectedTypeOfAccommodationOption.value === TypeOfAccommodation.OWNER
    );
  }

  dateValidation(formGroup: FormGroup) {
    const startDate = formGroup.get('startDate')?.value;
    const endDate = formGroup.get('endDate')?.value;

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      formGroup.controls['startDate'].setErrors({ invalidDateRange: true });
      formGroup.controls['endDate'].setErrors({ invalidDateRange: true });
    }

    return null;
  }

  onSelectedAccommodationOptionChange(): void {
    if (this.isAccommondationOwner()) {
      this.formGroup.addControl(
        'totalMortgage',
        new FormControl(String(this.kalpAnswers?.totalMortgage || ''), [
          Validators.required,
          FormValidators.number(0, 999999),
        ])
      );
      this.formGroup.addControl(
        'mortgagePaymentPerMonth',
        new FormControl(
          String(this.kalpAnswers?.mortgagePaymentPerMonth || ''),
          [Validators.required, FormValidators.number(0, 999999)]
        )
      );
      this.shouldShowMortgageInputs = true;
    } else {
      this.formGroup.removeControl('totalMortgage');
      this.formGroup.removeControl('mortgagePaymentPerMonth');
      this.shouldShowMortgageInputs = false;
    }
  }

  onSelectedOccupationOptionChange(
    newSelectedOption: PillSelectorOption
  ): void {
    switch (newSelectedOption.value) {
      case Occupation.FULL_TIME_EMPLOYMENT:
      case Occupation.PART_TIME_EMPLOYMENT:
      case Occupation.SELF_EMPLOYED:
      case Occupation.ENTREPRENEUR:
      case Occupation.OTHER:
        this.shouldDisableEndDate = true;
        this.resetEndDateForm(); // Resets EndDate if it was changed earlier.
        this.addDateControllers();
        break;
      case Occupation.FIX_TERM_EMPLOYMENT:
      case Occupation.RETIRED:
        this.shouldDisableEndDate = false;
        this.addDateControllers();
        break;

      case Occupation.STUDENT:
      case Occupation.UNEMPLOYED:
        this.formGroup.removeControl('startDate');
        this.formGroup.removeControl('endDate');
        this.formGroup.removeControl('nameOfEmployer');

        this.shouldShowOccupationInputs = false;
      default:
        break;
    }
  }

  addDateControllers(): void {
    if (!this.shouldShowOccupationInputs) {
      // Re-add Date and nameOfEmployer input controllers.
      this.formGroup.addControl(
        'startDate',
        new FormControl(
          this.kalpAnswers?.employmentStartDate
            ? formatDate(
                this.kalpAnswers.employmentStartDate,
                'yyyy-MM-dd',
                'en'
              )
            : '',
          [Validators.required]
        )
      );
      this.formGroup.addControl(
        'endDate',
        new FormControl(
          this.kalpAnswers?.employmentEndDate
            ? formatDate(this.kalpAnswers.employmentEndDate, 'yyyy-MM-dd', 'en')
            : '',
          !this.shouldDisableEndDate ? [Validators.required] : []
        )
      );
      this.formGroup.addControl(
        'nameOfEmployer',
        new FormControl(String(this.kalpAnswers?.nameOfEmployer || ''), [
          Validators.required,
          Validators.maxLength(255),
        ])
      );

      this.shouldShowOccupationInputs = true;
    }
  }

  getOccupationOptionsTranslations(occupation: Occupation): string {
    switch (occupation) {
      case Occupation.FULL_TIME_EMPLOYMENT:
        return this.translations.reviewOccupationFullTime;
      case Occupation.PART_TIME_EMPLOYMENT:
        return this.translations.reviewOccupationPartTime;
      case Occupation.FIX_TERM_EMPLOYMENT:
        return this.translations.reviewoccupationFixedTerm;
      case Occupation.SELF_EMPLOYED:
        return this.translations.occupationSelfemployed;
      case Occupation.STUDENT:
        return this.translations.occupationStudent;
      case Occupation.RETIRED:
        return this.translations.occupationRetired;
      case Occupation.UNEMPLOYED:
        return this.translations.occupationUnemployed;
    }
  }

  getMartialStatusOptionsTranslations(martialStatus: MartialStatus): string {
    switch (martialStatus) {
      case MartialStatus.SINGLE:
        return this.translations.martialStatusSingle;
      case MartialStatus.MARRIED:
        return this.translations.martialStatusMarried;
      case MartialStatus.COHABITANT:
        return this.translations.martialStatusCohabitant;
    }
  }

  getTypeOfAccommodationOtionsTranslations(
    typeOfAccommodation: TypeOfAccommodation
  ): string {
    switch (typeOfAccommodation) {
      case TypeOfAccommodation.RENT:
        return this.translations.typeOfAccommodationRental;
      case TypeOfAccommodation.OWNER:
        return this.translations.typeOfAccommodationOwner;
      case TypeOfAccommodation.PARTNER_OWNS_PROPERTY:
        return this.translations.typeOfAccommodationPartnerOwned;
      case TypeOfAccommodation.OTHER:
        return this.translations.typeOfAccommodationOther;
    }
  }

  async handleCtaClickReview(): Promise<void> {
    this.loading = true;

    try {
      await this.fiUserService
        .postKalpAnswers(this.kalpAnswers)
        .then(() => this.navigate());
    } catch (err) {
      this.error = true;
      this.handleError(err);
    }
  }

  handleUpdateClick(): void {
    this.viewReviewAnswers = false;
  }

  getNumberFromDecimalString(answer?: string): number | undefined {
    let formattedAnswer: string;

    if (answer) {
      formattedAnswer = answer.replace(',', '.').replace(/\s+/g, '');
    }

    return formattedAnswer ? Number(formattedAnswer) : undefined;
  }
}
